/**
 * THE TUTORIAL TEST SAVE — an admin plays a brand-new game without touching their real one.
 *
 * Started from the admin dashboard ("Start a throwaway game"), which clears the old sandbox row on the server and
 * sets the flag below; quit or restart from the in-game Dev menu. While it is on, in THIS TAB only:
 *   • every API call carries `x-save-sandbox: tutorial` (sim/save.ts `authHeaders`). The server reads and writes the
 *     save row `sandbox:<player>` instead of the real one, admin-only, and refuses any other write (petals, the shop,
 *     redeem codes, the profile) so nothing done in the sandbox reaches the real account (server/index.js
 *     `sandboxGate`);
 *   • the local mirror, the kept "lost save" copy and the one-active-tab lock use their own `…:sandbox` keys, so the
 *     real garden open in another tab is neither overwritten nor locked out.
 * The flag lives in sessionStorage: closing the tab ends the sandbox; another tab opens the real game.
 */
const KEY = "shelf-garden.sandbox";
export const SANDBOX_HEADER = "x-save-sandbox";
export const SANDBOX_KIND = "tutorial";

/**
 * Is THIS PAGE a test save? Read ONCE per page load and fixed from then on: switching (`setSandbox`) takes effect on
 * the next load. It used to be read at every write, so flipping the flag with a game still open sent that game's
 * unload flush into the OTHER save — the real garden copied into the sandbox, or the sandbox over the real garden.
 */
let _active: boolean | null = null;
export function sandboxActive(): boolean {
  if (_active === null) {
    try { _active = typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY) === SANDBOX_KIND; } catch { _active = false; }
  }
  return _active;
}
/** Switch this tab to (or away from) the test save FROM ITS NEXT LOAD — the caller reloads. */
export function setSandbox(on: boolean): void {
  try { if (on) sessionStorage.setItem(KEY, SANDBOX_KIND); else sessionStorage.removeItem(KEY); } catch { /* storage blocked: no sandbox */ }
}
/** Suffix an owner id (mirror key, lost-save key, play lock) while the sandbox is on. */
export function sandboxOwner(owner: string): string {
  return sandboxActive() ? `${owner}:sandbox` : owner;
}

/**
 * START AT A TUTORIAL STEP (sim/tutorialStages.ts): the stage a fresh test save should open on. Stashed for the next
 * load of this tab and taken once — GameLoader builds the staged save in place of the empty one.
 */
const STAGE_KEY = "shelf-garden.sandbox.stage";
export function setSandboxStage(stage: string | null): void {
  try { if (stage) sessionStorage.setItem(STAGE_KEY, stage); else sessionStorage.removeItem(STAGE_KEY); } catch { /* no stage */ }
}
export function takeSandboxStage(): string | null {
  try { const s = sessionStorage.getItem(STAGE_KEY); sessionStorage.removeItem(STAGE_KEY); return s; } catch { return null; }
}

/** Forget this browser's local copies of the test save (its mirror and kept "lost save"), so a restart starts empty. */
export function clearSandboxLocal(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("shelf-garden.") && k.endsWith(":sandbox")) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch { /* storage blocked — nothing kept */ }
}
